import { clsx } from "clsx";
import { readableColor, shade, tint } from "polished";
import { type FC, forwardRef, useEffect, useState } from "react";
import {
  type AriaButtonProps,
  mergeProps,
  type TooltipTriggerProps,
  useButton,
  useFocusRing,
  useHover,
  VisuallyHidden,
} from "react-aria";
import { useOverlayTriggerState } from "react-stately";

import { useDOMRef } from "../../hooks.ts";
import { getCssColor } from "../../utils.ts";
import { ActionButtonIntent } from "../ActionButton/settings.ts";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Popover } from "../Popover/Popover.tsx";
import style from "./ColorButton.module.css";
import { ColorPicker } from "./ColorPicker.tsx";

export interface ColorButtonProps extends AriaButtonProps, TooltipTriggerProps {
  defaultColor?: string;
  size?: "small" | "tiny";
  onOpen?: () => void;
  onApply?: (color: string) => void;
  onClose?: () => void;
  /** Handle click on color button without opening the color picker */
  onClick?: () => void;
  isOpen?: boolean;
  /** used to correct the button color only, the actual color setting remains unchanged */
  colorCorrectionFunc?: (color: string) => string;
}

export const ColorButton: FC<ColorButtonProps> = forwardRef<HTMLButtonElement, ColorButtonProps>(
  function ColorButton(props, ref) {
    const { defaultColor, colorCorrectionFunc } = props;

    const state = useOverlayTriggerState({
      isOpen: props.isOpen,
      onOpenChange: (isOpen) => {
        props.onOpenChange?.(isOpen);
        if (isOpen) {
          props.onOpen?.();
        } else {
          props.onClose?.();
        }
      },
    });

    const buttonRef = useDOMRef(ref);
    const { buttonProps } = useButton(
      { ...props, onPress: () => (props.onClick ? props.onClick() : state.open()) },
      buttonRef,
    );
    const { isFocusVisible, focusProps } = useFocusRing();
    const { hoverProps, isHovered } = useHover(props);

    const [colorText, setColorText] = useState<string | undefined>(
      defaultColor ? getCssColor(defaultColor) : undefined,
    );

    const [buttonColorText, setButtonColorText] = useState<string | undefined>(
      defaultColor
        ? colorCorrectionFunc
          ? colorCorrectionFunc(getCssColor(defaultColor))
          : getCssColor(defaultColor)
        : undefined,
    );

    const txtColor = colorText
      ? readableColor(colorText, shade(0.9, colorText), tint(0.9, colorText), false)
      : undefined;

    useEffect(() => {
      if (defaultColor) {
        const color = defaultColor ? getCssColor(defaultColor) : undefined;
        setColorText(color);
        setButtonColorText(colorCorrectionFunc && color ? colorCorrectionFunc(color) : color);
      }
    }, [defaultColor, colorCorrectionFunc]);
    return (
      <>
        <button
          data-testid="color-button"
          className={clsx(style.button, ActionButtonIntent.Outlined, style.color_picker, {
            [style.focus]: isFocusVisible,
            [style.hover]: isHovered,
            [style.disabled]: props.isDisabled,
            [style.mini]: props.size === "small" || props.size == undefined,
            [style.tiny]: props.size === "tiny",
            [style.no_color]: colorText === "" || colorText == undefined,
          })}
          {...mergeProps(buttonProps, focusProps, hoverProps)}
          ref={buttonRef}
        >
          <div
            style={{
              backgroundColor: buttonColorText, // may be corrected, if a correction function was provided
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <VisuallyHidden>Color: {colorText}</VisuallyHidden>
            {!!(props.onApply || props.onClick) && (
              <SymbolIcon
                data-testid="color-button-visually-hidden-icon"
                icon="colorize"
                size={props.size === "tiny" ? 16 : 20}
                style={{ color: txtColor ?? "var(--icon-default)" }}
              />
            )}
          </div>
        </button>
        <Popover data-testid="color-button-popover" state={state} triggerRef={buttonRef} placement="top">
          <ColorPicker
            defaultColor={colorText}
            closeFn={() => {
              props.onClose?.();
              state.close();
            }}
            onApply={(color) => {
              setColorText(color);
              props.onApply?.(color);
            }}
          />
        </Popover>
      </>
    );
  },
);
